import React, { useMemo } from 'react';
import type { CrudAddModalProps, CrudEditModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { useUpgradeService } from '../../services/UpgradeService';
import type {
	UpgradePathDiscountListFragment,
	UpgradePromptCreateInput,
	UpgradePromptListFragment,
	UpgradePromptUpdateInput,
} from '../../graphql/graphql';
import { PromptFrequency, UpgradePromptType } from '../../graphql/graphql';
import { Alert, Button, Group, Loader, NumberInput, Select, Stack, Switch, Textarea, TextInput } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import z from 'zod';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';

// This modal allows admins to create and edit upgrade prompts (banners, modals, toasts).
// Prompts are the marketing layer on top of upgrade paths - they control what users see and when.
// The form manages:
// - Prompt type (BANNER, MODAL, TOAST) and display frequency
// - Copy (title, message, CTA text)
// - Timing (show from/until day after purchase)
// - Targeting (which subscription types see the prompt)
// - Link to an upgrade path (which discount applies)
// Validation ensures timing windows are logical and required fields are present.
const MAX_PROMPT_PRIORITY = 1000;

const validationSchema = z
	.object({
		name: z.string().min(1).max(512),
		upgradePathDiscountId: z.string().min(1),
		promptType: z.nativeEnum(UpgradePromptType),
		frequency: z.nativeEnum(PromptFrequency),
		showFromDayAfterPurchase: z.number().min(0),
		showUntilDayAfterPurchase: z.union([z.number().min(0), z.literal('')]),
		// Optional content fields - empty string is valid (will be stored as null)
		title: z.string().max(512),
		message: z.string(),
		ctaText: z.string().max(512),
		isActive: z.boolean(),
		priority: z.number().min(0).max(MAX_PROMPT_PRIORITY),
	})
	.refine(
		(data) => {
			const from = data.showFromDayAfterPurchase;
			const until = typeof data.showUntilDayAfterPurchase === 'number' ? data.showUntilDayAfterPurchase : null;
			if (until !== null) {
				return until >= from;
			}
			return true;
		},
		{
			message: 'Show until day must be greater than or equal to show from day',
			path: ['showUntilDayAfterPurchase'],
		},
	);

type BaseProps = {
	paths: UpgradePathDiscountListFragment[];
};

type AddProps = CrudAddModalProps<UpgradePromptListFragment, UpgradePromptListFragment> & BaseProps;
type EditProps = CrudEditModalProps<UpgradePromptListFragment, UpgradePromptListFragment> & BaseProps;

type FormValues = {
	name: string;
	upgradePathDiscountId: string;
	promptType: UpgradePromptType;
	frequency: PromptFrequency;
	showFromDayAfterPurchase: number;
	showUntilDayAfterPurchase: number | '';
	title: string;
	message: string;
	ctaText: string;
	isActive: boolean;
	priority: number;
};

export function AddUpgradePromptModal(props: AddProps) {
	return <UpgradePromptModal {...props} mode="add" />;
}

export function EditUpgradePromptModal(props: EditProps) {
	return <UpgradePromptModal key={props.current.id} {...props} mode="edit" />;
}

type ModalProps = (AddProps | EditProps) & { mode: 'add' | 'edit' };

function UpgradePromptModal(props: ModalProps) {
	const { mode, paths, onCancel } = props;
	const current = mode === 'edit' && 'current' in props ? props.current : undefined;
	const upgradeService = useUpgradeService();
	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const initialValues = useMemo<FormValues>(() => {
		if (!current) {
			return {
				name: '',
				upgradePathDiscountId: paths[0]?.id ?? '',
				promptType: UpgradePromptType.Banner,
				frequency: PromptFrequency.Once,
				showFromDayAfterPurchase: 0,
				showUntilDayAfterPurchase: '',
				title: '',
				message: '',
				ctaText: '',
				isActive: true,
				priority: 0,
			};
		}

		return {
			name: current.name,
			upgradePathDiscountId: current.upgradePathDiscountId,
			promptType: current.promptType,
			frequency: current.frequency,
			showFromDayAfterPurchase: current.showFromDayAfterPurchase,
			showUntilDayAfterPurchase: current.showUntilDayAfterPurchase ?? '',
			title: current.title ?? '',
			message: current.message ?? '',
			ctaText: current.ctaText ?? '',
			isActive: current.isActive,
			priority: current.priority,
		};
	}, [current, paths]);

	const form = useForm<FormValues>({
		initialValues,
		validate: schemaResolver(validationSchema),
	});

	const handleCancel = () => {
		onCancel?.();
	};

	const handleSubmit = async (values: FormValues) => {
		const loader = addLoader();
		try {
			if (mode === 'add') {
				const created = await upgradeService.createPrompt(buildCreateInput(values));
				if ('onCreated' in props) {
					props.onCreated(created as UpgradePromptListFragment);
				}
				NotificationUtils.showSuccess('Upgrade prompt created', created.name);
			} else if (mode === 'edit' && current && 'onEdited' in props) {
				const updated = await upgradeService.updatePrompt(current.id, buildUpdateInput(values));
				props.onEdited(updated as UpgradePromptListFragment);
				NotificationUtils.showSuccess('Upgrade prompt updated', updated.name);
			}
		} catch (error) {
			const errorMessage = (error as Error).message || 'Unknown error';
			NotificationUtils.showError(error as Error, `Failed to save upgrade prompt: ${errorMessage}`);
		} finally {
			removeLoader(loader);
		}
	};

	const pathOptions = useMemo(() => paths.map((path) => ({ label: path.name, value: path.id })), [paths]);

	const isFormLoading = isLoading;
	const disableForm = isFormLoading || pathOptions.length === 0;

	return (
		<form onSubmit={form.onSubmit(handleSubmit)}>
			<Stack gap="sm">
				<TextInput {...form.getInputProps('name')} label="Prompt label" withAsterisk disabled={disableForm} />
				<Select
					{...form.getInputProps('upgradePathDiscountId')}
					data={pathOptions}
					label="Associated upgrade path"
					withAsterisk
					disabled={disableForm}
					searchable
					maxDropdownHeight={300}
				/>
				{!pathOptions.length && !isFormLoading && (
					<Alert color="yellow" variant="light">
						Create an upgrade path before configuring prompts.
					</Alert>
				)}
				<Select
					{...form.getInputProps('promptType')}
					data={Object.values(UpgradePromptType)}
					label="Prompt type"
					disabled={disableForm}
				/>
				<Group grow align="flex-end">
					<Select
						{...form.getInputProps('frequency')}
						data={Object.values(PromptFrequency)}
						label="Frequency"
						disabled={disableForm}
					/>
					<NumberInput
						{...form.getInputProps('priority')}
						label="Priority"
						min={0}
						max={1000}
						disabled={disableForm}
					/>
				</Group>
				<Group grow align="flex-end">
					<NumberInput
						{...form.getInputProps('showFromDayAfterPurchase')}
						label="Show from day"
						min={0}
						withAsterisk
						disabled={disableForm}
					/>
					<NumberInput
						{...form.getInputProps('showUntilDayAfterPurchase')}
						label="Show until day"
						min={0}
						placeholder="No limit"
						disabled={disableForm}
					/>
				</Group>
				<TextInput {...form.getInputProps('title')} label="Title" disabled={disableForm} />
				<Textarea
					{...form.getInputProps('message')}
					label="Message"
					autosize
					minRows={3}
					disabled={disableForm}
				/>
				<TextInput {...form.getInputProps('ctaText')} label="CTA text" disabled={disableForm} />
				<Switch
					label="Prompt is active"
					checked={form.values.isActive}
					onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
					disabled={disableForm}
				/>
				<Group justify="flex-end" mt="md">
					<Button variant="subtle" type="button" onClick={handleCancel} disabled={isFormLoading}>
						Cancel
					</Button>
					<Button type="submit" disabled={disableForm}>
						Save {isFormLoading && <Loader ml="xs" size="sm" />}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}

function buildCreateInput(values: FormValues): UpgradePromptCreateInput {
	return {
		name: values.name,
		upgradePathDiscount: { connect: { id: values.upgradePathDiscountId } },
		promptType: values.promptType,
		frequency: values.frequency,
		showFromDayAfterPurchase: values.showFromDayAfterPurchase,
		showUntilDayAfterPurchase:
			typeof values.showUntilDayAfterPurchase === 'number' ? values.showUntilDayAfterPurchase : undefined,
		// Convert empty strings to undefined (Prisma stores as NULL)
		title: values.title || undefined,
		message: values.message || undefined,
		ctaText: values.ctaText || undefined,
		isActive: values.isActive,
		priority: values.priority,
	};
}

function buildUpdateInput(values: FormValues): UpgradePromptUpdateInput {
	return {
		name: { set: values.name },
		upgradePathDiscount: { connect: { id: values.upgradePathDiscountId } },
		promptType: { set: values.promptType },
		frequency: { set: values.frequency },
		showFromDayAfterPurchase: { set: values.showFromDayAfterPurchase },
		showUntilDayAfterPurchase: {
			set: typeof values.showUntilDayAfterPurchase === 'number' ? values.showUntilDayAfterPurchase : null,
		},
		// Convert empty strings to null for storage
		title: { set: values.title || null },
		message: { set: values.message || null },
		ctaText: { set: values.ctaText || null },
		isActive: { set: values.isActive },
		priority: { set: values.priority },
	};
}
